import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
    Languages, User, Mail, Coins, ShoppingCart, Clock, Download,
    FileText, ArrowLeft, LogOut, CheckCircle, AlertTriangle, Loader2,
    RefreshCw, Globe, FileOutput, Trash2, Shield, Gift
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000')
    .replace(/\/+$/, '')
    .replace(/\/api$/, '');

const LANG_NAMES = {
    tr: 'Türkçe', en: 'İngilizce', de: 'Almanca', fr: 'Fransızca',
    es: 'İspanyolca', it: 'İtalyanca', ar: 'Arapça', ru: 'Rusça',
    zh_CN: 'Çince', ja: 'Japonca', ko: 'Korece', pt: 'Portekizce',
};
const langName = (code) => LANG_NAMES[code] || code?.toUpperCase() || '—';

const SHOPIFY_TOKEN_URL = 'https://your-shopify-store.com/products/tokens';

const ProfilePage = () => {
    const navigate = useNavigate();
    const { user, token, logout, refreshUser } = useAuth();
    const [translations, setTranslations] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(true);
    const [downloadingId, setDownloadingId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);

    const [couponCode, setCouponCode] = useState('');
    const [couponStatus, setCouponStatus] = useState(null);
    const [redeeming, setRedeeming] = useState(false);

    const handleRedeemCoupon = async (e) => {
        e.preventDefault();
        if (!couponCode.trim()) return;
        setRedeeming(true);
        setCouponStatus(null);
        try {
            const res = await fetch(`${API_BASE}/api/coupons/redeem`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ code: couponCode })
            });
            const data = await res.json();
            if (res.ok) {
                setCouponStatus({ type: 'success', message: data.message });
                setCouponCode('');
                refreshUser();
            } else {
                setCouponStatus({ type: 'error', message: data.error });
            }
        } catch (err) {
            setCouponStatus({ type: 'error', message: 'Bağlantı hatası.' });
        } finally {
            setRedeeming(false);
        }
    };

    useEffect(() => {
        if (token) fetchHistory();
    }, [token]);

    const fetchHistory = async () => {
        setLoadingHistory(true);
        try {
            const res = await fetch(`${API_BASE}/api/translations`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setTranslations(data);
            }
        } catch (err) {
            console.error('Geçmiş yüklenemedi:', err);
        } finally {
            setLoadingHistory(false);
        }
    };

    const handleDownload = async (t) => {
        if (t.is_expired || t.status !== 'completed') return;
        setDownloadingId(t.id);
        try {
            const res = await fetch(`${API_BASE}/api/download/${t.download_id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) {
                const err = await res.json();
                alert(err.error || 'İndirme başarısız.');
                return;
            }
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = t.filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            alert('İndirme hatası: ' + err.message);
        } finally {
            setDownloadingId(null);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Bu kaydı ve dosyayı silmek istediğinize emin misiniz?')) return;
        setDeletingId(id);
        try {
            const res = await fetch(`${API_BASE}/api/translations/${id}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${token}` }
            });
            if (res.ok) {
                setTranslations(prev => prev.filter(t => t.id !== id));
            } else {
                const err = await res.json();
                alert(err.error || 'Silme işlemi başarısız.');
            }
        } catch (err) {
            alert('Silme hatası: ' + err.message);
        } finally {
            setDeletingId(null);
        }
    };

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const formatDate = (iso) => {
        if (!iso) return '—';
        return new Date(iso).toLocaleString('tr-TR', {
            day: '2-digit', month: '2-digit', year: 'numeric',
            hour: '2-digit', minute: '2-digit'
        });
    };

    if (!user) return null;

    return (
        <div className="min-h-screen bg-soft-blue p-4 md:p-8">
            <div className="max-w-4xl mx-auto">

                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div className="flex items-center gap-3">
                        <img src="/logo192.png" alt="CeviriPro Logo" className="w-10 h-10 object-contain drop-shadow-sm" />
                        <div>
                            <h1 className="text-2xl font-bold text-gradient-blue">
                                CeviriPro
                            </h1>
                            <p className="text-xs text-slate-500">Profilim</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {user.is_admin && (
                            <Link
                                to="/admin"
                                className="flex items-center gap-2 px-4 py-2 bg-indigo-50 border border-indigo-100 rounded-xl text-indigo-700 hover:bg-indigo-100 transition-all text-sm font-bold shadow-sm"
                            >
                                <Shield className="w-4 h-4" />
                                Admin Paneli
                            </Link>
                        )}
                        <Link
                            to="/editor"
                            className="flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm border border-blue-100 rounded-xl text-slate-700 hover:bg-blue-50 hover:border-blue-200 transition-all text-sm font-medium shadow-sm"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            Editöre Dön
                        </Link>
                        <button
                            onClick={handleLogout}
                            className="flex items-center gap-2 px-4 py-2 bg-red-50/80 backdrop-blur-sm border border-red-100 rounded-xl text-red-600 hover:bg-red-100 transition-all text-sm font-medium"
                        >
                            <LogOut className="w-4 h-4" />
                            Çıkış
                        </button>
                    </div>
                </div>

                {/* User Info Card */}
                <div className="card-soft rounded-2xl p-6 mb-6">
                    <div className="flex items-start justify-between">
                        <div className="flex items-center gap-4">
                            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full flex items-center justify-center shadow-lg">
                                <span className="text-2xl font-bold text-white">
                                    {user.first_name?.[0]?.toUpperCase()}{user.last_name?.[0]?.toUpperCase()}
                                </span>
                            </div>
                            <div>
                                <h2 className="text-xl font-bold text-slate-900">
                                    {user.first_name} {user.last_name}
                                </h2>
                                <div className="flex items-center gap-1.5 text-slate-500 text-sm mt-1">
                                    <Mail className="w-3.5 h-3.5" />
                                    <span>{user.email}</span>
                                </div>
                                <div className="flex items-center gap-1.5 mt-2">
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                                        <CheckCircle className="w-3 h-3" /> Doğrulanmış Hesap
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Token Card */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
                    <div className="bg-gradient-to-r from-blue-500 via-indigo-500 to-cyan-500 rounded-2xl shadow-lg p-6 text-white flex flex-col justify-center">
                        <div className="flex items-center gap-2 mb-2 opacity-80">
                            <Coins className="w-5 h-5 flex-shrink-0" />
                            <span className="text-sm font-medium">Mevcut Token Bakiyesi</span>
                        </div>
                        <div className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-1 break-all">
                            {(user.tokens_remaining ?? 0).toLocaleString('tr-TR')}
                        </div>
                        <div className="text-blue-100 text-sm">
                            Toplam satın alınan: <span className="font-semibold text-white">{(user.total_tokens_purchased ?? 0).toLocaleString('tr-TR')}</span>
                        </div>
                        <a
                            href={SHOPIFY_TOKEN_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-4 flex items-center justify-center gap-2 w-full py-2.5 bg-white text-blue-600 rounded-xl font-semibold text-sm hover:shadow-lg transition-all"
                        >
                            <ShoppingCart className="w-4 h-4" />
                            Token Satın Al
                        </a>
                    </div>

                    <div className="card-soft rounded-2xl p-6 flex flex-col justify-between">
                        <div>
                            <div className="flex items-center gap-2 mb-4 text-slate-800">
                                <Gift className="w-5 h-5 text-indigo-500" />
                                <span className="font-bold">Kupon Kodu Kullan</span>
                            </div>
                            <p className="text-sm text-slate-500 mb-4">Satın aldığınız kodu girerek bakiyenize ekleyin.</p>

                            <form onSubmit={handleRedeemCoupon} className="flex flex-col gap-3">
                                <input
                                    type="text"
                                    placeholder="CPN-..."
                                    value={couponCode}
                                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                                    className="w-full p-3 border border-slate-200 rounded-xl focus:border-indigo-500 focus:ring-0 text-slate-800 font-mono text-sm"
                                    disabled={redeeming}
                                />
                                <button
                                    type="submit"
                                    disabled={redeeming || !couponCode.trim()}
                                    className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 text-sm"
                                >
                                    {redeeming ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                                    Kodu Doğrula
                                </button>
                            </form>

                            {couponStatus && (
                                <div className={`mt-3 p-3 rounded-xl text-xs font-semibold flex items-start gap-2 ${couponStatus.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
                                    {couponStatus.type === 'success' ? <CheckCircle className="w-4 h-4 flex-shrink-0" /> : <AlertTriangle className="w-4 h-4 flex-shrink-0" />}
                                    <span>{couponStatus.message}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="card-soft rounded-2xl p-6">
                        <div className="flex items-center gap-2 mb-4 text-slate-500">
                            <FileText className="w-5 h-5" />
                            <span className="text-sm font-medium">Çeviri İstatistikleri</span>
                        </div>
                        <div className="text-4xl font-bold text-gradient-blue mb-1">{translations.length}</div>
                        <div className="text-slate-500 text-sm">Toplam çeviri işlemi</div>
                        <div className="mt-4 space-y-2">
                            <div className="flex justify-between text-sm">
                                <span className="text-slate-500">Aktif dosyalar</span>
                                <span className="font-semibold text-blue-600">
                                    {translations.filter(t => !t.is_expired && t.status === 'completed').length}
                                </span>
                            </div>
                            <div className="flex justify-between text-sm">
                                <span className="text-slate-500">Bekleyenler</span>
                                <span className="font-semibold text-indigo-600">
                                    {translations.filter(t => t.status === 'pending' || t.status === 'processing').length}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Translation History */}
                <div className="card-soft rounded-2xl border border-blue-100 overflow-hidden">
                    <div className="flex items-center justify-between p-5 border-b border-blue-100">
                        <h3 className="font-bold text-slate-900 flex items-center gap-2">
                            <Clock className="w-5 h-5 text-blue-500" />
                            Çeviri Geçmişi
                        </h3>
                        <button
                            onClick={fetchHistory}
                            className="p-2 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Yenile"
                        >
                            <RefreshCw className="w-4 h-4 text-slate-500" />
                        </button>
                    </div>

                    {loadingHistory ? (
                        <div className="flex items-center justify-center p-12">
                            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                        </div>
                    ) : translations.length === 0 ? (
                        <div className="text-center p-12 text-slate-400">
                            <FileText className="w-12 h-12 mx-auto mb-3 opacity-30" />
                            <p className="font-medium text-slate-500">Henüz çeviri yapmadınız</p>
                            <p className="text-sm mt-1">Editör'den ilk çevirinizi yapın!</p>
                        </div>
                    ) : (
                        <div className="divide-y divide-blue-50">
                            {translations.map((t) => (
                                <div
                                    key={t.id}
                                    className={`flex items-center justify-between p-4 hover:bg-blue-50/50 transition-colors ${t.is_expired ? 'opacity-60' : ''}`}
                                >
                                    <div className="flex items-start gap-3 flex-1 min-w-0">
                                        <div className={`p-2 rounded-lg flex-shrink-0 ${t.operation === 'translate' ? 'bg-purple-100' : 'bg-blue-100'}`}>
                                            {t.operation === 'translate'
                                                ? <Globe className="w-4 h-4 text-purple-600" />
                                                : <FileOutput className="w-4 h-4 text-blue-600" />
                                            }
                                        </div>
                                        <div className="min-w-0">
                                            <p className="font-medium text-slate-800 text-sm truncate" title={t.filename || t.original_filename}>
                                                {t.filename || t.original_filename}
                                            </p>
                                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${t.output_format === 'pdf' ? 'bg-red-100 text-red-700' :
                                                    t.output_format === 'docx' ? 'bg-blue-100 text-blue-700' :
                                                        t.output_format === 'epub' ? 'bg-green-100 text-green-700' :
                                                            'bg-slate-100 text-slate-700'
                                                    }`}>
                                                    {t.output_format || 'TXT'}
                                                </span>
                                                {t.operation === 'translate' && (
                                                    <span className="text-xs text-slate-500">
                                                        {langName(t.source_lang)} → {langName(t.target_lang)}
                                                    </span>
                                                )}
                                                <span className="text-xs text-slate-400">
                                                    {t.char_count?.toLocaleString()} karakter
                                                </span>
                                                <span className="text-xs text-slate-400">
                                                    {formatDate(t.created_at)}
                                                </span>
                                            </div>

                                            {t.status === 'pending' || t.status === 'processing' ? (
                                                <span className="inline-flex items-center gap-1 mt-1 text-xs text-indigo-600 font-bold animate-pulse">
                                                    <Clock className="w-3 h-3" />
                                                    Sırada İşleniyor... (Tahmini {t.estimated_minutes} dk)
                                                </span>
                                            ) : t.is_expired ? (
                                                <span className="inline-flex items-center gap-1 mt-1 text-xs text-red-500 font-medium">
                                                    <AlertTriangle className="w-3 h-3" />
                                                    Süre doldu — dosya silindi
                                                </span>
                                            ) : (
                                                <span className="inline-flex items-center gap-1 mt-1 text-xs text-blue-600 font-medium">
                                                    <Clock className="w-3 h-3" />
                                                    {t.hours_remaining}s içinde silinecek
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => handleDownload(t)}
                                            disabled={t.is_expired || downloadingId === t.id || t.status !== 'completed'}
                                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex-shrink-0
                                                ${t.is_expired || t.status !== 'completed'
                                                    ? 'bg-slate-100 text-slate-400 cursor-not-allowed'
                                                    : 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white hover:shadow-lg hover:shadow-blue-500/30'
                                                }`}
                                        >
                                            {downloadingId === t.id
                                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                : <Download className="w-3.5 h-3.5" />
                                            }
                                            İndir
                                        </button>

                                        <button
                                            onClick={() => handleDelete(t.id)}
                                            disabled={deletingId === t.id}
                                            className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all"
                                            title="Sil"
                                        >
                                            {deletingId === t.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProfilePage;
